import React, { forwardRef } from "react"
import { TreeSelect as AntdTreeSelect } from "antd"
import cs from "classnames"

import * as StyleSheet from "../styles.js"
import Wrapper from "../Wrapper"
/**
 * TreeSelect Component for selecting options from a tree structure
 *
 * @param {string} name - used to build the unique test id of the internal components
 * @param {any} value - currently selected value
 * @param {Function} onChange - returns currently selected value when a selection is made
 * @param {Boolean} disabled - Whether or not the input is disabled
 * @param {Object} theme - Styling theme (ie. "profile", "agents", "search", "searchBox").  Defaults to "profile"
 * @param {string} label - Label for the TreeSelect component
 * @param {Array<Object>} options - Options to be displayed in the tree structure
 * @param {Object} className - CSS classes to apply to the parent element
 * @param {Boolean} showSearch - Whether to show search box, default is true
 * @param {Boolean} allowClear - Whether to show clear button, default is true
 */
const TreeSelect = forwardRef(({
  name,
  onChange = () => null,
  disabled = false,
  theme,
  label = "",
  options,
  className,
  showSearch = true,
  allowClear = true,
  ...rest
}, ref) => {
  const styleSheet = StyleSheet[theme]?.(disabled) || StyleSheet["profile"](disabled)

  return (
    <Wrapper label={label} className={className} name={`treeSelect-${name}`} styleSheet={styleSheet}>
      <AntdTreeSelect
        ref={ref}
        treeData={options}
        className={cs(
          "p-0 pl-0 pr-0 text-left",
          "[&.ant-select-focused>.ant-select-selector]:border-blue-200",
          "[&.ant-select-focused>.ant-select-selector]:ring-2",
          "[&.ant-select-focused>.ant-select-selector]:ring-blue-200",
          "transition",
          styleSheet.input
        )}
        showSearch={showSearch}
        allowClear={allowClear}
        onChange={onChange}
        disabled={disabled}
        variant="borderless"
        treeNodeFilterProp="title"
        {...rest}
      />
    </Wrapper>
  )
})
TreeSelect.displayName = "TreeSelect"

export default TreeSelect
